/**
 * INSERT column-list demotion for columns the ENGINE generates.
 *
 * An `IDENTITY` / computed / generated column is not a legal INSERT target:
 * T-SQL rejects `INSERT INTO t (LogID) …` outright unless the session first
 * runs `SET IDENTITY_INSERT t ON`, and it rejects a computed column with no
 * escape hatch at all.
 *
 * The honest encoding is a TIER DROP, not a filter. Two reasons:
 *   - a writer who is about to turn `IDENTITY_INSERT` on genuinely needs the
 *     name, and a completion list that has silently deleted it is worse than
 *     one that ranks it last;
 *   - the flag can be absent (`undefined` — a snapshot taken before the
 *     introspection carried it, a relation the bus does not describe), and a
 *     filter built on absent data deletes legal answers.
 * So the columns stay in the list, one tier below the insertable ones, with
 * the reason in `detail`. Sibling of `filterAlreadyTypedInsertColumns`
 * (already-typed names — those ARE illegal with no escape hatch, so they are
 * dropped) and of the ranker's own "column-first positions" demote rule.
 *
 * Engine-neutral by construction: the caller supplies the flags, so any engine
 * whose adapter populates `ColumnMeta.isIdentity` / `isComputed` (T-SQL
 * `IDENTITY`, PG `GENERATED ALWAYS AS IDENTITY`, MySQL `AUTO_INCREMENT`) can
 * wire it. Only the OUTPUT/RETURNING-free column list is affected — an
 * `OUTPUT INSERTED.LogID` list wants the identity column at full weight, and
 * that caret is claimed by its own emitter before this runs.
 */

import type { CompletionCandidate, CompletionCandidateTier, DatabaseEngineId } from '@/core/engines';

import { findInsertColumnListTarget } from '../context/findTypedInsertColumns';

/** Where a demoted column lands: one tier below the insertable columns. */
const DEMOTED_TIER: CompletionCandidateTier = 2;

/** The subset of a snapshot table this module reads. */
export type InsertableColumnTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly isIdentity?: boolean;
    readonly isComputed?: boolean;
  }>;
};

export type DemoteGeneratedInsertColumnsArgs = {
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly statementEnd: number;
  readonly engine: DatabaseEngineId;
  readonly tables: readonly InsertableColumnTable[];
};

/**
 * Demote the target relation's engine-generated columns in place. No-op
 * (returns false) when the caret is not inside an INSERT column list, when the
 * target is not in the snapshot, or when it has no generated column.
 */
export function demoteGeneratedInsertColumns(
  candidates: CompletionCandidate[],
  args: DemoteGeneratedInsertColumnsArgs,
): boolean {
  const generated = generatedColumnsAtInsertTarget(args);
  if (generated.size === 0) return false;
  let demoted = false;
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i]!;
    if (c.kind !== 'column') continue;
    const reason = generated.get(c.label.toLowerCase());
    if (!reason) continue;
    candidates[i] = {
      ...c,
      tier: DEMOTED_TIER,
      detail: appendReason(c.detail, reason, args.engine),
    };
    demoted = true;
  }
  return demoted;
}

/**
 * Lower-cased generated column names of the INSERT column list's target,
 * mapped to the reason they are not insertable. Empty when the caret is not in
 * such a list or the target cannot be resolved. Exported for unit tests.
 */
export function generatedColumnsAtInsertTarget(
  args: DemoteGeneratedInsertColumnsArgs,
): ReadonlyMap<string, GeneratedColumnReason> {
  const target = findInsertColumnListTarget(
    args.text,
    args.offset,
    args.statementStart,
    args.statementEnd,
    args.engine,
  );
  if (!target) return EMPTY;
  const table = args.tables.find(
    (t) =>
      eqName(t.name, target.table) &&
      (target.schema === undefined || eqName(t.schema ?? '', target.schema)),
  );
  if (!table) return EMPTY;
  const out = new Map<string, GeneratedColumnReason>();
  for (const col of table.columns) {
    // IDENTITY is reported first: a column can be both (a computed column
    // cannot carry IDENTITY, but the flags come from independent catalog bits
    // and the reason text should be deterministic either way).
    if (col.isIdentity) out.set(col.name.toLowerCase(), 'identity');
    else if (col.isComputed) out.set(col.name.toLowerCase(), 'computed');
  }
  return out;
}

export type GeneratedColumnReason = 'identity' | 'computed';

const EMPTY: ReadonlyMap<string, GeneratedColumnReason> = new Map();

const REASON_TEXT: Record<GeneratedColumnReason, string> = {
  identity: 'IDENTITY — needs SET IDENTITY_INSERT',
  computed: 'computed — not insertable',
};

/**
 * The escape hatch differs per dialect, and the detail line is user-facing
 * text, so it cannot claim T-SQL's. On PostgreSQL the flag also covers
 * `SERIAL` (a `nextval()` default), where an explicit value simply overrides
 * the sequence and no escape hatch is needed at all; a real `GENERATED ALWAYS
 * AS IDENTITY` column takes `OVERRIDING SYSTEM VALUE`. Engines without an
 * entry get the neutral T-SQL-free wording.
 */
const REASON_TEXT_BY_ENGINE: Partial<
  Record<DatabaseEngineId, Partial<Record<GeneratedColumnReason, string>>>
> = {
  pgsql: {
    identity: 'value assigned by the engine — an explicit value overrides it',
    computed: 'generated — not insertable',
  },
};

function appendReason(
  detail: string | undefined,
  reason: GeneratedColumnReason,
  engine: DatabaseEngineId,
): string {
  const text = REASON_TEXT_BY_ENGINE[engine]?.[reason] ?? REASON_TEXT[reason];
  return detail ? `${detail} · ${text}` : text;
}

function eqName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}
